/**
 * IndexedDBAdapter.ts - IndexedDB Storage Adapter for Cachr
 * 
 * CHANGE HISTORY:
 * - 251125_2213 danielbeer: Add TypeScript types
 * - 251019_1122 [0b22d83] danielbeer: feat: add IndexedDB and hybrid storage adapters
 */
import type { IndexedDBOptions, StorageAdapter } from './types';

const DB_NAME = 'CachrDB';
const DB_VERSION = 1;
const STORE_NAME = 'cache';

export class IndexedDBAdapter implements StorageAdapter {
  private dbName: string;
  private storeName: string;
  private db: IDBDatabase | null;
  private initPromise: Promise<IDBDatabase>;

  constructor(options: IndexedDBOptions = {}) {
    this.dbName = options.dbName || DB_NAME;
    this.storeName = options.storeName || STORE_NAME;
    this.db = null;
    this.initPromise = this.init();
  }

  /**
   * Initialize IndexedDB connection
   */
  async init(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB failed to open:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'key' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is ready
   */
  async ensureReady(): Promise<void> {
    if (!this.db) {
      await this.initPromise;
    }
  }

  /**
   * Get item from IndexedDB
   */
  async getItem(key: string): Promise<string | null> {
    await this.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result?.value || null);
      };

      request.onerror = () => {
        console.error('IndexedDB getItem error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Set item in IndexedDB
   */
  async setItem(key: string, value: string): Promise<void> {
    await this.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const item = {
        key,
        value,
        timestamp: Date.now(),
        lastAccessed: Date.now()
      };

      const request = store.put(item);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('IndexedDB setItem error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Remove item from IndexedDB
   */
  async removeItem(key: string): Promise<void> {
    await this.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('IndexedDB removeItem error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all keys from IndexedDB
   */
  async getAllKeys(): Promise<string[]> {
    await this.ensureReady();

    return new Promise<string[]>((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('IndexedDB getAllKeys error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all items from IndexedDB
   */
  async clear(): Promise<void> {
    await this.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('IndexedDB clear error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get items sorted by lastAccessed (for LRU eviction)
   */
  async getItemsByLastAccessed(limit: number | null = null): Promise<any[]> {
    await this.ensureReady();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('lastAccessed');
      const request = index.openCursor();
      
      const items = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        
        if (cursor && (!limit || items.length < limit)) {
          items.push({
            key: cursor.value.key,
            lastAccessed: cursor.value.lastAccessed,
            timestamp: cursor.value.timestamp
          });
          cursor.continue();
        } else {
          resolve(items);
        }
      };

      request.onerror = () => {
        console.error('IndexedDB getItemsByLastAccessed error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}
